import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const ChangeEmailModal = ({ isOpen, onClose, currentEmail, onEmailChange }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const handleEmailChange = async (e) => {
    e.preventDefault();
    
    if (newEmail === currentEmail) {
      toast.error('New email is the same as current email');
      return;
    }

    if (!newEmail.includes('@') || !newEmail.includes('.')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      await onEmailChange(newEmail);
      setNewEmail('');
      onClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Mail className="w-5 h-5 text-blue-600" />
            <span>Change Email Address</span>
          </DialogTitle>
          <DialogDescription>
            You'll need to verify your new email address before the change takes effect.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleEmailChange} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="current_email">Current Email</Label>
            <Input
              id="current_email"
              type="email"
              value={currentEmail}
              disabled
              className="bg-gray-50 mt-1"
            />
          </div>

          <div>
            <Label htmlFor="new_email">New Email Address</Label>
            <Input
              id="new_email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter your new email address"
              className="mt-1"
              required
            />
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Important:</p>
                <ul className="space-y-1 text-xs">
                  <li>• We'll send a verification link to your new email</li>
                  <li>• Your current email will remain active until verified</li>
                  <li>• We'll notify your current email about this change</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !newEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Send Verification Email
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeEmailModal;
